import { escapeHtmlAttr } from '@shared/utils/helpers';

/**
 * Represents a product returned by the CatalogApiController.GetProducts endpoint.
 */
export interface Product {
    id: string;
    name: string;
    mediaUrl: string;
    defaultPrice: number;
    undiscountedPrice: number;
    hasSpecialPrice: boolean;
    seoFriendlyId: string;
    available: boolean;
    isWeightUnit: boolean;
    unitOfMeasureName: string;
    /** Detail page URL, built by the server because only routing knows a tenant's product URL shape. */
    url: string;
}

/**
 * Configuration options read from the data attributes of the product grid container element.
 */
export interface ProductGridConfig {
    /** Maximum number of products to display. */
    maxItems: number;
    /** Assortment ID used to load products. */
    assortment: string;
    /** URL of the GetProducts API endpoint. */
    apiUrl: string;
    /** Item the grid relates to, sent as itemId. Empty for assortment based grids. */
    itemId: string;
}

/**
 * Manages a dynamic product grid.
 * Reads configuration from the container's data attributes, fetches products
 * from the CatalogApi endpoint and renders product cards into the grid.
 * Also handles carousel navigation (previous / next) with dot indicator updates
 * and a 5 % scale-up effect on the active product image.
 */
export class ProductGridManager {
    private readonly container: HTMLElement;
    private readonly config: ProductGridConfig;

    /** Zero-based index of the currently selected product card. */
    private currentIndex: number = 0;

    /** Total number of rendered product cards. */
    private totalItems: number = 0;

    /**
     * @param container - The DOM element that wraps the product grid and holds data attributes.
     */
    constructor(container: HTMLElement) {
        this.container = container;
        this.config = this.readConfig();
        this.initialize();
    }

    /**
     * Reads the data attributes from the container element and builds the configuration object.
     *
     * @returns The parsed configuration for this grid instance.
     */
    private readConfig(): ProductGridConfig {
        const dataset = this.container.dataset;

        return {
            maxItems: parseInt(dataset.maxItems ?? '4', 10),
            assortment: dataset.assortment ?? '',
            apiUrl: dataset.apiUrl ?? '/CatalogApi/GetProducts',
            itemId: dataset.itemId ?? '',
        };
    }

    /**
     * An optional section wraps the grid in an element marked `data-product-grid-section`. Such a section
     * starts hidden and is only revealed once products were loaded, so it never leaves an empty block
     * behind. Grids without that wrapper render their empty and error states as usual.
     */
    private getOptionalSection(): HTMLElement | null {
        return this.container.closest<HTMLElement>('[data-product-grid-section]');
    }

    /**
     * An optional section that stays hidden may name a replacement via `data-fallback-target` (a CSS
     * selector). That element is revealed instead, so the page never ends up with neither of the two.
     *
     * @param section - The optional section that has nothing to show.
     */
    private revealFallback(section: HTMLElement): void {
        const selector = section.dataset.fallbackTarget;
        if (!selector) return;

        document.querySelector<HTMLElement>(selector)?.classList.remove('hidden');
    }

    /**
     * Initialises the component: renders a skeleton loader, fetches data and then renders the result.
     *
     * @returns A promise that resolves when initialisation is complete.
     */
    private async initialize(): Promise<void> {
        this.renderSkeleton();

        try {
            const products = await this.fetchProducts();
            this.renderProducts(products);
        } catch (error) {
            console.error('[ProductGrid] Failed to load products:', error);
            this.renderError();
        }
    }

    /**
     * Fetches products from the CatalogApiController.GetProducts endpoint.
     *
     * @returns A promise resolving to an array of Product objects.
     */
    private async fetchProducts(): Promise<Product[]> {
        const params = new URLSearchParams({
            assortment: this.config.assortment,
            count: String(this.config.maxItems),
        });

        if (this.config.itemId) {
            params.set('itemId', this.config.itemId);
        }

        const response = await fetch(`${this.config.apiUrl}?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`API responded with status ${response.status}`);
        }

        return await response.json() as Product[];
    }

    /**
     * Renders skeleton placeholder cards while the real data is being fetched.
     * The number of skeletons matches the configured maxItems value.
     */
    private renderSkeleton(): void {
        const grid = this.container.querySelector<HTMLElement>('.product-group-grid');
        if (!grid) return;

        grid.innerHTML = '';

        for (let i = 0; i < this.config.maxItems; i++) {
            const card = document.createElement('div');
            card.className = 'flex flex-col gap-4 min-w-[298px] snap-start animate-pulse';
            card.innerHTML = `
                <div class="h-[373px] rounded-2xl w-full bg-gray-200"></div>
                <div class="flex flex-col gap-2">
                    <div class="h-5 bg-gray-200 rounded w-3/4"></div>
                    <div class="h-5 bg-gray-200 rounded w-1/4"></div>
                </div>`;
            grid.appendChild(card);
        }
    }

    /**
     * Renders product cards from the fetched data into the grid element.
     *
     * @param products - The list of products to render.
     */
    private renderProducts(products: Product[]): void {
        const grid = this.container.querySelector<HTMLElement>('.product-group-grid');
        if (!grid) return;

        grid.innerHTML = '';

        const section = this.getOptionalSection();

        if (products.length === 0) {
            if (section) {
                this.revealFallback(section);
                return;
            }

            this.renderEmpty(grid);
            return;
        }

        section?.classList.remove('hidden');

        products.forEach(product => {
            const card = this.createProductCard(product);
            grid.appendChild(card);
        });

        this.totalItems = products.length;
        this.currentIndex = 0;

        // Update carousel dots to reflect the actual number of items
        this.updateCarouselDots(products.length);

        // Apply the selected state to the first card and wire up navigation buttons
        this.selectProduct(0);
        this.bindCarouselButtons();
    }

    /**
     * Formats a decimal price value as a localized currency string (e.g. "3,90 €").
     *
     * @param price - The numeric price value.
     * @returns A formatted price string.
     */
    private formatPrice(price: number): string {
        return price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '\u00a0€';
    }

    /**
     * Creates a single product card element.
     *
     * @param product - The product data to render.
     * @returns A fully constructed HTMLElement representing the card.
     */
    private createProductCard(product: Product): HTMLElement {
        const card = document.createElement('div');
        card.className = 'flex flex-col gap-4 min-w-[298px] snap-start';
        card.dataset.productId = product.id;

        const productUrl = product.url;

        const hasImage = product.mediaUrl
            && !product.mediaUrl.includes('00000000-0000-0000-0000-000000000000');

        const imageSrc = hasImage
            ? product.mediaUrl
            : 'https://images.unsplash.com/photo-1509440159596-0249088772ff?q=80&w=600&auto=format&fit=crop';

        // Show crossed-out original price when a special price is active
        const priceHtml = product.hasSpecialPrice
            ? `<p class="font-['Asap'] font-semibold text-[#f46647] text-[20px] leading-[1.3] tracking-[-0.2px]">
                   ${escapeHtmlAttr(this.formatPrice(product.defaultPrice))}
               </p>
               <p class="font-['Asap'] font-normal text-[#646e76] text-[14px] line-through">
                   ${escapeHtmlAttr(this.formatPrice(product.undiscountedPrice))}
               </p>`
            : `<p class="font-['Asap'] font-semibold text-[#303e48] text-[20px] leading-[1.3] tracking-[-0.2px]">
                   ${escapeHtmlAttr(this.formatPrice(product.defaultPrice))}
               </p>`;

        card.innerHTML = `
            <div class="flex flex-col items-end relative w-full">
                <div class="product-card-image-wrapper h-[373px] relative rounded-2xl w-full overflow-hidden group">
                    <a href="${productUrl}">
                        <img
                            src="${escapeHtmlAttr(imageSrc)}"
                            alt="${escapeHtmlAttr(product.name)}"
                            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy">
                    </a>
                </div>
                <a href="${productUrl}"
                   class="product-shop-btn absolute bottom-[10px] right-4 bg-company flex items-center justify-center rounded-full size-12 hover:bg-company-hover text-company-contrast transition-colors"
                   title="${escapeHtmlAttr(product.name)}"
                   aria-label="Zum Produkt ${escapeHtmlAttr(product.name)}">
                        <i class="fa-solid fa-cart-arrow-down"></i>
                </a>
            </div>
            <div class="flex flex-col gap-2 items-start w-full">
                <h3 class="font-['Asap'] font-semibold text-[#303e48] text-[20px] leading-[1.3] tracking-[-0.2px] w-full">
                    <a href="${productUrl}" class="hover:text-company transition-colors">
                        ${escapeHtmlAttr(product.name)}
                    </a>
                </h3>
                ${priceHtml}
            </div>`;

        return card;
    }

    /**
     * Selects the product card at the given index, updates the dot indicators
     * and applies the 5 % zoom effect to the active product image.
     * Clears the zoom on all other cards.
     *
     * @param index - Zero-based index of the card to activate.
     */
    private selectProduct(index: number): void {
        this.currentIndex = index;

        const grid = this.container.querySelector<HTMLElement>('.product-group-grid');
        if (!grid) return;

        const cards = Array.from(grid.querySelectorAll<HTMLElement>('[data-product-id]'));

        cards.forEach((card, i) => {
            const imageWrapper = card.querySelector<HTMLElement>('.product-card-image-wrapper');
            const img = imageWrapper?.querySelector<HTMLImageElement>('img');
            if (!img) return;

            if (i === index) {
                // Apply 5 % scale-up to the selected product image
                img.classList.add('scale-[1.05]');
            } else {
                img.classList.remove('scale-[1.05]');
            }
        });

        // Scroll the grid so the selected card is fully visible (no scrollbar required)
        const activeCard = cards[index];
        if (activeCard) {
            const cardLeft = activeCard.offsetLeft;
            const cardWidth = activeCard.offsetWidth;
            const gridWidth = grid.offsetWidth;

            // Center the active card within the visible grid area
            const targetScroll = cardLeft - (gridWidth / 2) + (cardWidth / 2);
            grid.scrollTo({ left: targetScroll, behavior: 'smooth' });
        }

        // Sync dot indicators
        const dotsContainer = this.container.querySelector<HTMLElement>('.product-group-dots');
        if (!dotsContainer) return;

        const dots = Array.from(dotsContainer.querySelectorAll<HTMLElement>('[data-dot-index]'));
        dots.forEach((dot, i) => {
            if (i === index) {
                dot.className = 'w-6 h-2 rounded-full bg-company transition-all duration-300';
            } else {
                dot.className = 'w-2 h-2 rounded-full bg-[#cbcfd1] transition-all duration-300';
            }
        });
    }

    /**
     * Binds click handlers to the Zurück and Weiter navigation buttons.
     * Navigation wraps around: after the last item it cycles back to the first,
     * and pressing Zurück on the first item jumps to the last.
     */
    private bindCarouselButtons(): void {
        const prevButton = this.container.querySelector<HTMLButtonElement>('[aria-label="Zurück"]');
        const nextButton = this.container.querySelector<HTMLButtonElement>('[aria-label="Weiter"]');

        if (prevButton) {
            // Remove any previously registered listener to avoid duplicates
            const newPrev = prevButton.cloneNode(true) as HTMLButtonElement;
            prevButton.replaceWith(newPrev);
            newPrev.addEventListener('click', () => {
                const prev = (this.currentIndex - 1 + this.totalItems) % this.totalItems;
                this.selectProduct(prev);
            });
        }

        if (nextButton) {
            const newNext = nextButton.cloneNode(true) as HTMLButtonElement;
            nextButton.replaceWith(newNext);
            newNext.addEventListener('click', () => {
                const next = (this.currentIndex + 1) % this.totalItems;
                this.selectProduct(next);
            });
        }
    }

    /**
     * Updates the carousel dot indicators to match the number of displayed products.
     * Each dot is clickable and navigates directly to the corresponding product card,
     * scrolling horizontally if needed.
     *
     * @param count - Total number of product cards rendered.
     */
    private updateCarouselDots(count: number): void {
        const dotsContainer = this.container.querySelector<HTMLElement>('.product-group-dots');
        if (!dotsContainer) return;

        dotsContainer.innerHTML = '';

        for (let i = 0; i < count; i++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = i === 0
                ? 'w-6 h-2 rounded-full bg-company transition-all duration-300 cursor-pointer'
                : 'w-2 h-2 rounded-full bg-[#cbcfd1] transition-all duration-300 cursor-pointer';
            dot.dataset.dotIndex = String(i);
            dot.setAttribute('aria-label', `Produkt ${i + 1} anzeigen`);

            // Navigate to the corresponding product card on click
            dot.addEventListener('click', () => {
                this.selectProduct(i);
            });

            dotsContainer.appendChild(dot);
        }
    }

    /**
     * Renders an empty-state message when the API returns no products.
     *
     * @param grid - The grid container element to render the message into.
     */
    private renderEmpty(grid: HTMLElement): void {
        grid.innerHTML = `
            <p class="font-['Asap'] text-[#646e76] text-[16px] col-span-full py-8 text-center w-full">
                Keine Produkte verfügbar.
            </p>`;
    }

    /**
     * Renders an error message inside the grid when the API call fails.
     */
    private renderError(): void {
        // An optional section stays hidden on failure instead of showing an error where nothing was promised.
        const section = this.getOptionalSection();
        if (section) {
            this.revealFallback(section);
            return;
        }

        const grid = this.container.querySelector<HTMLElement>('.product-group-grid');
        if (!grid) return;

        grid.innerHTML = `
            <p class="font-['Asap'] text-red-500 text-[16px] col-span-full py-8 text-center w-full">
                Die Produkte konnten nicht geladen werden. Bitte versuche es später erneut.
            </p>`;
    }

}

